/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { RELATIONSHIP_MODEL_VERSION } from '../constants';
import type {
  FilterBinding,
  ModelNode,
  ModelRelationship,
  RelationshipModel,
} from '../types';

export type ModelAction =
  | { type: 'SET_MODEL'; model: RelationshipModel }
  | { type: 'ADD_NODE'; node: ModelNode }
  | { type: 'REMOVE_NODE'; nodeId: string }
  | {
      type: 'UPDATE_NODE_POSITION';
      nodeId: string;
      position: { x: number; y: number };
    }
  | { type: 'ADD_RELATIONSHIP'; relationship: ModelRelationship }
  | {
      type: 'UPDATE_RELATIONSHIP';
      relationshipId: string;
      patch: Partial<ModelRelationship>;
    }
  | { type: 'REMOVE_RELATIONSHIP'; relationshipId: string }
  | { type: 'SET_PROJECTIONS'; nodeId: string; outputColumns: string[] }
  | { type: 'SET_FILTER_BINDING'; binding: FilterBinding }
  | { type: 'REMOVE_FILTER_BINDING'; nativeFilterId: string }
  | { type: 'SET_OUTPUT_MAIN_DTTM_COL'; column?: string };

export function createEmptyModel(): RelationshipModel {
  return {
    version: RELATIONSHIP_MODEL_VERSION,
    nodes: [],
    relationships: [],
    filterBindings: [],
  };
}

export function relationshipReducer(
  state: RelationshipModel,
  action: ModelAction,
): RelationshipModel {
  switch (action.type) {
    case 'SET_MODEL':
      return action.model;
    case 'ADD_NODE':
      if (state.nodes.some(n => n.id === action.node.id)) {
        return state;
      }
      return { ...state, nodes: [...state.nodes, action.node] };
    case 'REMOVE_NODE':
      return {
        ...state,
        nodes: state.nodes.filter(n => n.id !== action.nodeId),
        relationships: state.relationships.filter(
          r =>
            r.leftNodeId !== action.nodeId && r.rightNodeId !== action.nodeId,
        ),
      };
    case 'UPDATE_NODE_POSITION':
      return {
        ...state,
        nodes: state.nodes.map(n =>
          n.id === action.nodeId ? { ...n, position: action.position } : n,
        ),
      };
    case 'ADD_RELATIONSHIP':
      if (state.relationships.some(r => r.id === action.relationship.id)) {
        return state;
      }
      return {
        ...state,
        relationships: [...state.relationships, action.relationship],
      };
    case 'UPDATE_RELATIONSHIP':
      return {
        ...state,
        relationships: state.relationships.map(r =>
          r.id === action.relationshipId ? { ...r, ...action.patch } : r,
        ),
      };
    case 'REMOVE_RELATIONSHIP':
      return {
        ...state,
        relationships: state.relationships.filter(
          r => r.id !== action.relationshipId,
        ),
      };
    case 'SET_PROJECTIONS':
      return {
        ...state,
        nodes: state.nodes.map(n =>
          n.id === action.nodeId
            ? { ...n, outputColumns: action.outputColumns }
            : n,
        ),
      };
    case 'SET_FILTER_BINDING': {
      const existing = state.filterBindings ?? [];
      const without = existing.filter(
        b => b.nativeFilterId !== action.binding.nativeFilterId,
      );
      return {
        ...state,
        filterBindings: [...without, action.binding],
      };
    }
    case 'REMOVE_FILTER_BINDING':
      return {
        ...state,
        filterBindings: (state.filterBindings ?? []).filter(
          b => b.nativeFilterId !== action.nativeFilterId,
        ),
      };
    case 'SET_OUTPUT_MAIN_DTTM_COL':
      return {
        ...state,
        outputMainDttmCol: action.column,
      };
    default:
      return state;
  }
}
